/**
 * ダッシュボード棒グラフ（t検定・ANOVA）の設定型と localStorage 読み書き
 */

import type { DashboardBarErrorBarType } from './keisukeDashboardChartUtils';
import { safeLocalStorageSetJson } from './keisukeDashboardLocalStorage';
import type { KeisukeUnifiedFontStyle } from '../charts/keisukeUnifiedChartFontApply';

function normalizeKeisukeFontStyle(
  v: unknown,
  fallback: KeisukeUnifiedFontStyle
): KeisukeUnifiedFontStyle {
  if (v === 'normal' || v === 'italic' || v === 'bold') return v;
  return fallback;
}

/** 高さを「自動」（null）にしたときの SVG 総高さの既定 px（従来の数値デフォルトと同じ） */
export const DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX = 500;

/**
 * 右ペイン（split-plot）でコンテナ追従するときの SVG 高さ上限。
 * 固定 size.height は split-plot では無視し、ツールバー下の親高に合わせる。
 */
export const MAX_SPLIT_PLOT_AUTO_HEIGHT_PX = 2400;

/** レガシー計助 ANOVAView と同等のグラフ設定型 */
export interface DashboardBarChartSettings {
  size: {
    width: number | null;
    /** null のときは自動（描画時は {@link DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX} を使用） */
    height: number | null;
    plotAreaWidth: number | null;
    plotAreaHeight: number | null;
  };
  xAxis: {
    label: string;
    lengthPercent: number;
    tickInterval: number | null;
    tickLabelRotation: number;
    tickLabelAnchor: 'start' | 'middle' | 'end';
    intersectionValue: number | null;
    labelPosition: number;
    lineWidth: number;
    grid: {
      show: boolean;
      lineStyle: 'solid' | 'dashed' | 'dotted';
      opacity: number;
      color: string;
    };
  };
  yAxis: {
    label: string;
    /** プロット領域の高さに対する Y 値スケール帯の長さ（%）。100 で従来どおり全高を使用 */
    lengthPercent: number;
    scaleType: 'linear' | 'log' | 'sqrt' | 'reciprocal';
    tickLabelFormat: 'auto' | 'exponential';
    tickLabelDigits: number | null;
    tickInterval: number | null;
    minValue: number | null;
    maxValue: number | null;
    intersectionValue: number | null;
    labelPosition: number;
    lineWidth: number;
    grid: {
      show: boolean;
      lineStyle: 'solid' | 'dashed' | 'dotted';
      opacity: number;
      color: string;
    };
  };
  textStyles: {
    axisLabel: {
      fontSize: number;
      fontWeight: string;
      fontStyle: KeisukeUnifiedFontStyle;
      fontFamily: string;
      color: string;
    };
    tickLabel: {
      fontSize: number;
      fontWeight: string;
      fontStyle: KeisukeUnifiedFontStyle;
      fontFamily: string;
      color: string;
    };
  };
  /**
   * SVG 周囲余白の手動補正（px）。自動計算した margin に加算される（負値で詰めることも可）
   */
  marginAdjust: { top: number; right: number; bottom: number; left: number };
  /** t検定・ANOVA: 棒 / 箱ひげ / 折れ線 / 散布図（散布図は個々の観測値を群位置にジッタープロット） */
  plot: { barWidth: number; barSpacing: number; categoryPlotKind: 'bar' | 'box' | 'line' | 'scatter' };
  groupDisplay: { barCenter: 'mean' | 'median'; errorBarType: DashboardBarErrorBarType };
  legend: {
    show: boolean;
    position: 'top' | 'bottom' | 'right' | 'free';
    freeOffsetXPct: number;
    freeOffsetYPct: number;
    textStyle: {
      fontSize: number;
      fontWeight: string;
      fontStyle: KeisukeUnifiedFontStyle;
      fontFamily: string;
    };
  };
  significance: {
    show: boolean;
    style: 'p' | 'asterisk';
    offset: number;
    gap: number;
    displayMode: 'all' | 'topN';
    topN: number;
  };
  /**
   * 参照線（プロット領域内）
   * - 水平: Y = 定数（対数・逆数Y軸では Y>0 のときのみ描画）
   * - 垂直: カテゴリ（群）ラベル位置。`verticalCategory` は現在の X 軸ラベル文字列と一致させる
   */
  referenceLine: {
    enabled: boolean;
    y: number | null;
    verticalEnabled: boolean;
    verticalCategory: string | null;
  };
  chartBackgroundColor: string;
}

/** Y 軸スケール種別（設定・localStorage・フォームで共用） */
export type DashboardBarChartYAxisScaleType = DashboardBarChartSettings['yAxis']['scaleType'];

/** 未知の値は fallback に落とす（localStorage 改ざん・将来の select 拡張に備える） */
export function normalizeDashboardBarChartYAxisScaleType(
  value: unknown,
  fallback: DashboardBarChartYAxisScaleType
): DashboardBarChartYAxisScaleType {
  if (value === 'linear' || value === 'log' || value === 'sqrt' || value === 'reciprocal') {
    return value;
  }
  return fallback;
}

/** `referenceLine` の欠損フィールドを既定で埋める（localStorage 旧形式互換） */
export function coalesceReferenceLine(
  r: Partial<DashboardBarChartSettings['referenceLine']> | undefined
): DashboardBarChartSettings['referenceLine'] {
  const def: DashboardBarChartSettings['referenceLine'] = {
    enabled: false,
    y: null,
    verticalEnabled: false,
    verticalCategory: null,
  };
  if (!r) return { ...def };
  const yRaw = r.y;
  const y =
    yRaw != null && Number.isFinite(Number(yRaw))
      ? Number(yRaw)
      : yRaw === null
        ? null
        : def.y;
  const vc = r.verticalCategory;
  const verticalCategory =
    typeof vc === 'string' && vc.trim() !== ''
      ? vc.trim()
      : vc === null || vc === ''
        ? null
        : def.verticalCategory;
  return {
    enabled: typeof r.enabled === 'boolean' ? r.enabled : def.enabled,
    y,
    verticalEnabled: typeof r.verticalEnabled === 'boolean' ? r.verticalEnabled : def.verticalEnabled,
    verticalCategory,
  };
}

export const STORAGE_KEY_BAR = 'keisuke_dashboard_bar_settings';
const STORAGE_KEY_BAR_LEGACY = 'keisuke_dashboard_legacy_bar_settings';
/** 旧既定 -45°/end → 水平 middle への一度きりの移行フラグ */
const STORAGE_KEY_BAR_X_TICK_HORIZONTAL_V1 = 'keisuke_dashboard_bar_x_tick_horizontal_v1';
/** 旧既定 X 軸 lengthPercent=70 → 100 への一度きりの移行フラグ */
const STORAGE_KEY_BAR_X_LENGTH_FULL_V1 = 'keisuke_dashboard_bar_x_length_full_v1';

const MARGIN_ADJUST_LIMIT = 200;

/** localStorage からの余白補正を正規化 */
export function normalizeMarginAdjust(
  raw: unknown
): DashboardBarChartSettings['marginAdjust'] {
  const def = { top: 0, right: 0, bottom: 0, left: 0 };
  if (!raw || typeof raw !== 'object') return def;
  const o = raw as Record<string, unknown>;
  const clamp = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseInt(String(v), 10);
    if (!Number.isFinite(n)) return 0;
    return Math.max(-MARGIN_ADJUST_LIMIT, Math.min(MARGIN_ADJUST_LIMIT, Math.round(n)));
  };
  return { top: clamp(o.top), right: clamp(o.right), bottom: clamp(o.bottom), left: clamp(o.left) };
}

/** localStorage 旧形式 barShowSd および groupDisplay マージ */
export function mergeGroupDisplay(
  def: DashboardBarChartSettings['groupDisplay'],
  parsed?: Partial<DashboardBarChartSettings['groupDisplay']> & { barShowSd?: boolean }
): DashboardBarChartSettings['groupDisplay'] {
  const p = parsed ?? {};
  const barCenter = p.barCenter === 'median' ? 'median' : 'mean';
  let errorBarType: DashboardBarErrorBarType;
  if (
    p.errorBarType === 'none' ||
    p.errorBarType === 'sd' ||
    p.errorBarType === 'se' ||
    p.errorBarType === 'ci'
  ) {
    errorBarType = p.errorBarType;
  } else if (typeof p.barShowSd === 'boolean') {
    errorBarType = p.barShowSd ? 'sd' : 'none';
  } else {
    errorBarType = def.errorBarType;
  }
  return { barCenter, errorBarType };
}

/**
 * t検定の推論手法に合わせた棒の中心・誤差棒。
 * ノンパラは中央値＋誤差なし（SD 誤差棒が結果の中央値表記と食い違うのを防ぐ）。
 */
export function groupDisplayForTTestInferential(
  isParametric: boolean
): DashboardBarChartSettings['groupDisplay'] {
  return isParametric
    ? { barCenter: 'mean', errorBarType: 'sd' }
    : { barCenter: 'median', errorBarType: 'none' };
}

export function createDefaultBarChartSettings(
  t: (key: string, opts?: { defaultValue?: string }) => string
): DashboardBarChartSettings {
  return {
    size: {
      width: null,
      height: null,
      plotAreaWidth: null,
      plotAreaHeight: null,
    },
    xAxis: {
      label: t('keisuke.chartAxisDefaults.xLabel'),
      lengthPercent: 100,
      tickInterval: null,
      /** 短い群名（WT/KO 等）は水平が読みやすい。長いラベルは設定で斜めにできる */
      tickLabelRotation: 0,
      tickLabelAnchor: 'middle',
      intersectionValue: null,
      labelPosition: 40,
      lineWidth: 3,
      grid: { show: false, lineStyle: 'dashed', opacity: 1, color: '#000000' },
    },
    yAxis: {
      label: t('keisuke.chartAxisDefaults.yLabel'),
      lengthPercent: 100,
      scaleType: 'linear',
      tickLabelFormat: 'auto',
      tickLabelDigits: null,
      tickInterval: null,
      minValue: null,
      maxValue: null,
      intersectionValue: null,
      labelPosition: -65,
      lineWidth: 3,
      grid: { show: false, lineStyle: 'dashed', opacity: 1, color: '#000000' },
    },
    textStyles: {
      axisLabel: { fontSize: 16, fontWeight: '500', fontStyle: 'normal', fontFamily: 'Arial', color: '#000000' },
      tickLabel: { fontSize: 16, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'Arial', color: '#000000' },
    },
    plot: { barWidth: 40, barSpacing: 0.5, categoryPlotKind: 'bar' },
    groupDisplay: { barCenter: 'mean', errorBarType: 'sd' },
    legend: {
      /* 棒は X 軸ラベルで群が分かる。同色凡例は冗長なので既定オフ */
      show: false,
      position: 'top',
      freeOffsetXPct: 88,
      freeOffsetYPct: 8,
      textStyle: { fontSize: 12, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'Arial' },
    },
    significance: {
      show: true,
      style: 'asterisk',
      offset: 50,
      gap: 24,
      displayMode: 'all',
      topN: 1,
    },
    referenceLine: { enabled: false, y: null, verticalEnabled: false, verticalCategory: null },
    marginAdjust: { top: 0, right: 0, bottom: 0, left: 0 },
    chartBackgroundColor: 'transparent',
  };
}

export function loadBarChartSettings(
  t: (key: string, opts?: { defaultValue?: string }) => string
): DashboardBarChartSettings {
  try {
    const saved =
      localStorage.getItem(STORAGE_KEY_BAR) ?? localStorage.getItem(STORAGE_KEY_BAR_LEGACY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<DashboardBarChartSettings>;
      const def = createDefaultBarChartSettings(t);
      let xAxis = {
        ...def.xAxis,
        ...parsed.xAxis,
        grid: { ...def.xAxis.grid, ...parsed.xAxis?.grid },
      };
      /** 旧既定の斜めラベルを、短い群名でも読みやすい水平に一度だけ寄せる */
      try {
        if (
          localStorage.getItem(STORAGE_KEY_BAR_X_TICK_HORIZONTAL_V1) !== '1' &&
          xAxis.tickLabelRotation === -45 &&
          (xAxis.tickLabelAnchor === 'end' || parsed.xAxis?.tickLabelAnchor == null)
        ) {
          xAxis = { ...xAxis, tickLabelRotation: 0, tickLabelAnchor: 'middle' };
          localStorage.setItem(STORAGE_KEY_BAR_X_TICK_HORIZONTAL_V1, '1');
        }
      } catch {
        // ignore
      }
      /** 旧既定 lengthPercent=70 はプロット内に横余白だけ残る → 一度だけ 100 へ */
      let migratedXLengthFull = false;
      try {
        if (
          localStorage.getItem(STORAGE_KEY_BAR_X_LENGTH_FULL_V1) !== '1' &&
          parsed.xAxis?.lengthPercent === 70
        ) {
          xAxis = { ...xAxis, lengthPercent: 100 };
          migratedXLengthFull = true;
          localStorage.setItem(STORAGE_KEY_BAR_X_LENGTH_FULL_V1, '1');
        }
      } catch {
        // ignore
      }
      const loaded: DashboardBarChartSettings = {
        ...def,
        ...parsed,
        size: { ...def.size, ...parsed.size },
        xAxis,
        yAxis: (() => {
          const merged = {
            ...def.yAxis,
            ...parsed.yAxis,
            grid: { ...def.yAxis.grid, ...parsed.yAxis?.grid },
          };
          const scaleType = normalizeDashboardBarChartYAxisScaleType(merged.scaleType, def.yAxis.scaleType);
          return { ...merged, scaleType };
        })(),
        textStyles: (() => {
          const axisLabel = {
            ...def.textStyles.axisLabel,
            ...parsed.textStyles?.axisLabel,
            fontStyle: normalizeKeisukeFontStyle(
              parsed.textStyles?.axisLabel?.fontStyle,
              def.textStyles.axisLabel.fontStyle
            ),
          };
          const tickLabelMerged = {
            ...def.textStyles.tickLabel,
            ...parsed.textStyles?.tickLabel,
            fontStyle: normalizeKeisukeFontStyle(
              parsed.textStyles?.tickLabel?.fontStyle,
              def.textStyles.tickLabel.fontStyle
            ),
          };
          const tickColorOk =
            typeof tickLabelMerged.color === 'string' && tickLabelMerged.color.trim() !== '';
          const axisColorOk = typeof axisLabel.color === 'string' && axisLabel.color.trim() !== '';
          return {
            axisLabel,
            tickLabel: {
              ...tickLabelMerged,
              color: tickColorOk
                ? tickLabelMerged.color
                : axisColorOk
                  ? axisLabel.color
                  : def.textStyles.tickLabel.color,
            },
          };
        })(),
        plot: {
          ...def.plot,
          ...parsed.plot,
          /**
           * グラフ種類はセッション内の表示切替。localStorage から復元しない。
           * （箱ひげ等を一度選ぶと、平均＋誤差棒の主経路が永続的に隠れるのを防ぐ）
           */
          categoryPlotKind: def.plot.categoryPlotKind,
        },
        groupDisplay: mergeGroupDisplay(def.groupDisplay, parsed.groupDisplay),
        legend: {
          ...def.legend,
          ...parsed.legend,
          freeOffsetXPct: parsed.legend?.freeOffsetXPct ?? def.legend.freeOffsetXPct,
          freeOffsetYPct: parsed.legend?.freeOffsetYPct ?? def.legend.freeOffsetYPct,
          textStyle: {
            ...def.legend.textStyle,
            ...parsed.legend?.textStyle,
            fontStyle: normalizeKeisukeFontStyle(
              parsed.legend?.textStyle?.fontStyle,
              def.legend.textStyle.fontStyle
            ),
          },
        },
        significance: { ...def.significance, ...parsed.significance },
        marginAdjust: normalizeMarginAdjust(parsed.marginAdjust),
        referenceLine: coalesceReferenceLine({
          ...def.referenceLine,
          ...parsed.referenceLine,
        }),
        chartBackgroundColor: parsed.chartBackgroundColor ?? def.chartBackgroundColor,
      };
      if (migratedXLengthFull) {
        safeLocalStorageSetJson(STORAGE_KEY_BAR, loaded);
      }
      return loaded;
    }
  } catch {
    // ignore
  }
  return createDefaultBarChartSettings(t);
}

/** 棒グラフ設定を localStorage に保存（容量超過・プライベートモード等は握りつぶす） */
export function saveBarChartSettings(settings: DashboardBarChartSettings): void {
  safeLocalStorageSetJson(STORAGE_KEY_BAR, settings);
}
